// Pinch zoom + finger pan for the game table (mobile).
//  - two fingers: pinch to zoom (1x-3x), and they also pan while zoomed
//  - one finger, when zoomed in: drag the empty table to scroll in any direction (up/down/left/right)
//  - one finger ON a card (or the hand): unchanged, so dragging/arranging cards and scrolling the hand still work
//  - a quick tap is never swallowed (we only start panning after a small movement)
//  - double-tap on empty table resets to normal size
import React, { useRef, useEffect } from 'react';

const MIN = 1;
const MAX = 3;
const CARD_SELECTOR = '.fan-card, .player-hand, .current-player-hand-container, button, input, select, textarea, [data-no-pan]';

const PinchZoom = ({ enabled = true, children }) => {
  const outerRef = useRef(null);
  const innerRef = useRef(null);
  const st = useRef({ s: 1, x: 0, y: 0, pinch: null, pan: null, lastTap: 0 });

  const apply = () => {
    const { s, x, y } = st.current;
    // At normal size use NO transform: a transform makes position:fixed children (the hand) measure from
    // this box instead of the screen, which pushed the hand below the visible area.
    if (innerRef.current) {
      innerRef.current.style.transform = (s === 1 && x === 0 && y === 0) ? 'none' : `translate(${x}px, ${y}px) scale(${s})`;
    }
  };
  const clampPan = (s, x, y) => {
    const r = outerRef.current.getBoundingClientRect();
    return { x: Math.min(0, Math.max(r.width * (1 - s), x)), y: Math.min(0, Math.max(r.height * (1 - s), y)) };
  };
  const reset = () => { Object.assign(st.current, { s: 1, x: 0, y: 0 }); apply(); };

  useEffect(() => {
    const el = outerRef.current;
    if (!enabled || !el) return undefined;
    const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
    const mid = (t) => {
      const r = el.getBoundingClientRect();
      return { x: (t[0].clientX + t[1].clientX) / 2 - r.left, y: (t[0].clientY + t[1].clientY) / 2 - r.top };
    };

    const onStart = (e) => {
      const S = st.current;
      if (e.touches.length === 2) {
        const c = mid(e.touches);
        S.pan = null;
        S.pinch = { d0: dist(e.touches), s0: S.s, px: (c.x - S.x) / S.s, py: (c.y - S.y) / S.s };
        e.preventDefault();
      } else if (e.touches.length === 1 && S.s > 1.02 && !e.target.closest(CARD_SELECTOR)) {
        S.pan = { sx: e.touches[0].clientX, sy: e.touches[0].clientY, x0: S.x, y0: S.y, active: false };
      }
    };

    const onMove = (e) => {
      const S = st.current;
      if (S.pinch && e.touches.length === 2) {
        e.preventDefault();
        const s = Math.min(MAX, Math.max(MIN, S.pinch.s0 * (dist(e.touches) / S.pinch.d0)));
        const c = mid(e.touches);
        const p = clampPan(s, c.x - S.pinch.px * s, c.y - S.pinch.py * s);   // two-finger drag pans too
        S.s = s; S.x = p.x; S.y = p.y; apply();
      } else if (S.pan && e.touches.length === 1) {
        const dx = e.touches[0].clientX - S.pan.sx, dy = e.touches[0].clientY - S.pan.sy;
        if (!S.pan.active && Math.hypot(dx, dy) < 6) return;              // let taps through
        S.pan.active = true;
        e.preventDefault();
        const p = clampPan(S.s, S.pan.x0 + dx, S.pan.y0 + dy);
        S.x = p.x; S.y = p.y; apply();
      }
    };

    const onEnd = (e) => {
      const S = st.current;
      if (e.touches.length < 2 && S.pinch) {
        S.pinch = null;
        if (S.s < 1.05) reset();
      }
      if (e.touches.length === 0) {
        const wasPanning = S.pan && S.pan.active;
        S.pan = null;
        // double-tap on the empty table => back to normal size
        if (!wasPanning && S.s > 1.02 && e.changedTouches.length === 1 && !e.target.closest(CARD_SELECTOR)) {
          const now = Date.now();
          if (now - S.lastTap < 300) { reset(); S.lastTap = 0; } else S.lastTap = now;
        }
      }
    };

    el.addEventListener('touchstart', onStart, { passive: false });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [enabled]);

  if (!enabled) return <>{children}</>;
  return (
    <div ref={outerRef} style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, overflow: 'hidden', touchAction: 'none' }}>
      <div ref={innerRef} style={{ width: '100%', height: '100%', transformOrigin: '0 0' }}>
        {children}
      </div>
    </div>
  );
};

export default PinchZoom;
